import React, { useState, useEffect, useRef } from "react";
import SetlistCard from '../components/SetlistCard';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FolderIcon, PlusIcon, SearchIcon, MoreVertical, Edit, Copy, Trash, Share, Download, ListPlus, ExternalLink, Youtube } from "lucide-react";
import { generatePrintUrl } from "../utils/PrintSettingsUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../contexts/AuthContext";
import { useUIState } from "../contexts/UIStateContext";
import { useSetlistActions } from "../contexts/ActionsContext";
import { useNotifications } from "../contexts/NotificationContext";
import { getAllSetlists, deleteSetlist, updateSetlist } from "../services/SetlistStorageService";
import ConfirmModal from "../components/ConfirmModal";
import YouTubePlaylistImportModal from "../components/YouTubePlaylistImportModal";
import bandService from "../services/BandService";
import { getAuthToken, isAuthenticated } from "../utils/AuthUtils";
import logger from "../services/LoggingService";

/**
 * Page displaying all available setlists categorized by ownership
 */
// Separate component for editing setlist names to isolate state and prevent unwanted re-renders
const EditableSetlistName = ({ name, isEditing, onSubmit, onCancel }) => {
  const [value, setValue] = useState(name || "");
  const inputRef = useRef(null);
  
  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  const handleSubmit = () => {
    onSubmit(value.trim());
  };
  
  if (!isEditing) {
    return <h2 className="text-lg font-medium truncate">{name || 'Untitled'}</h2>;
  }
  
  return (
    <div 
      className="w-full" 
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <input
        ref={inputRef}
        type="text"
        className="w-full border border-primary rounded px-2 py-1"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            handleSubmit();
          } else if (e.key === "Escape") {
            onCancel();
          }
        }}
        onBlur={handleSubmit}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      />
    </div>
  );
};

const SetlistsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotifications();
  const [searchTerm, setSearchTerm] = useState("");
  const { isAuthenticated, showAuthModal, authChangeCounter, currentUser } = useAuth();
  const { handleContextMenu: uiHandleContextMenu } = useUIState();
  const { setSetlistActions } = useSetlistActions();
  const [isLoading, setIsLoading] = useState(true);
  const [mySetlists, setMySetlists] = useState([]);
  const [bandSetlists, setBandSetlists] = useState([]);
  const [otherSetlists, setOtherSetlists] = useState([]);
  
  // State for setlist editing and dropdown menu
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const inputRef = useRef(null);
  
  // State for confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    setlistId: null
  });
  
  // State for full setlist editing (name + description + band assignment)
  const [editingSetlist, setEditingSetlist] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    band_id: '',
    isPublic: true
  });
  const [userBands, setUserBands] = useState([]);
  
  // State for YouTube playlist import modal
  const [showYouTubeImportModal, setShowYouTubeImportModal] = useState(false);
  
  // Load user's bands when authenticated
  useEffect(() => {
    const loadUserBands = async () => {
      if (isAuthenticated) {
        try {
          const bands = await bandService.getUserBands();
          setUserBands(bands);
        } catch (error) {
          console.error('Error loading user bands:', error);
        }
      } else {
        setUserBands([]);
      }
    };
    
    loadUserBands();
  }, [isAuthenticated]);
  
  // Register the setlist actions with the context
  useEffect(() => {
    if (setSetlistActions) {
      setSetlistActions({
        handleCreateSetlist: () => {
          handleCreateSetlist();
        },
        handleYouTubeImport: () => {
          setShowYouTubeImportModal(true);
        }
      });
    }
  }, [setSetlistActions]);
  
  // Fetch setlists from the API directly
  useEffect(() => {
    const fetchSetlists = async () => {
      setIsLoading(true);
      try {
        // Get current authentication status - use the property from context, not the function
        const token = getAuthToken();
        logger.debug('SetlistsPage', 'Current auth state before fetching setlists:', {
          isAuthenticatedProp: isAuthenticated, // Use the property from useAuth context
          tokenExists: !!token,
          tokenFirstChars: token ? token.substring(0, 10) + '...' : 'N/A'
        });
        
        // Get all setlists from the API
        logger.debug('SetlistsPage', 'Attempting to fetch setlists from MongoDB...');
        const setlists = await getAllSetlists();
        logger.debug('SetlistsPage', 'Fetched setlists from API:', setlists);
        logger.debug('SetlistsPage', 'Number of setlists retrieved:', setlists ? setlists.length : 0);
        logger.debug('SetlistsPage', 'Setlist data type:', typeof setlists);
        
        if (setlists && setlists.length > 0) {
          logger.debug('SetlistsPage', 'First setlist preview:', {
            _id: setlists[0]._id,
            name: setlists[0].name,
            sheetCount: setlists[0].sheets ? setlists[0].sheets.length : 0
          });
        }
        
        // Now properly categorize the setlists based on ownership
        logger.debug('SetlistsPage', 'Using setlists from MongoDB');
        logger.debug('SetlistsPage', 'Current user details:', { 
          isAuthenticated: isAuthenticated, // Using the context property
          userId: isAuthenticated ? 'has-auth-token' : 'not-authenticated'
        });
        
        if (setlists && setlists.length > 0) {
          // Debug data structure of the first setlist if available
          if (setlists.length > 0) {
            console.log('DETAILED SETLIST INSPECTION:');
            console.log('First setlist object:', JSON.stringify(setlists[0], null, 2));
            
            const firstSetlistOwner = setlists[0].owner;
            console.log('Owner structure type:', typeof firstSetlistOwner);
            if (firstSetlistOwner) {
              console.log('Owner fields:', Object.keys(firstSetlistOwner));
            }
          }
          
          console.log('Current authenticated user status:', isAuthenticated); // Using the context property
          
          // Split setlists into categories
          const mySetlistsArray = [];
          const bandSetlistsArray = [];
          const otherSetlistsArray = [];

          // Build a set of the user's band IDs for quick lookup
          const userBandIds = new Set(userBands.map(b => b._id?.toString()));

          // Categorize setlists
          setlists.forEach(setlist => {
            const ownerObj = setlist.owner;
            const ownerId = ownerObj?._id || (typeof ownerObj === 'string' ? ownerObj : null);
            const currentUserId = currentUser?._id;
            const isMySetlist = isAuthenticated && currentUserId && ownerId &&
              (ownerId === currentUserId || ownerId.toString() === currentUserId.toString());

            // Check if setlist belongs to one of the user's bands
            const isBandSetlist = setlist.band_id && userBandIds.has(setlist.band_id.toString());

            if (isMySetlist) {
              mySetlistsArray.push(setlist);
              // Also add to band setlists if it has a band_id matching user's bands
              if (isBandSetlist) {
                bandSetlistsArray.push(setlist);
              }
            } else if (isBandSetlist) {
              bandSetlistsArray.push(setlist);
            } else {
              otherSetlistsArray.push(setlist);
            }
          });
          
          console.log(`Categorized setlists: My(${mySetlistsArray.length}), Band(${bandSetlistsArray.length}), Other(${otherSetlistsArray.length})`);
          
          // Update state with categorized setlists
          setMySetlists(mySetlistsArray);
          setBandSetlists(bandSetlistsArray);
          setOtherSetlists(otherSetlistsArray);
        } else {
          // No setlists found
          setMySetlists([]);
          setBandSetlists([]);
          setOtherSetlists([]);
        }
      } catch (error) {
        console.error('Error fetching setlists:', error);
        // Try to get more info about the error
        if (error.response) {
          console.error('Response error data:', error.response.data);
          console.error('Response error status:', error.response.status);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSetlists();
  }, [authChangeCounter, isAuthenticated, userBands]); // Refetch when auth state or bands change

  // Handle URL parameters for editing
  useEffect(() => {
    const editSetlistId = searchParams.get('edit');
    console.log('URL parameter handling:', { editSetlistId, mySetlistsLength: mySetlists.length, bandSetlistsLength: bandSetlists.length, isLoading });
    
    if (editSetlistId && !isLoading && (mySetlists.length > 0 || bandSetlists.length > 0)) {
      // Find the setlist to edit in either my setlists or band setlists
      const setlistToEdit = [...mySetlists, ...bandSetlists].find(setlist => setlist._id === editSetlistId);
      console.log('Found setlist to edit:', setlistToEdit);
      
      if (setlistToEdit) {
        startEditSetlist(setlistToEdit);
        // Clear the URL parameter
        setSearchParams({});
      } else {
        console.warn('Setlist not found for editing:', editSetlistId);
      }
    }
  }, [searchParams, mySetlists, bandSetlists, setSearchParams, isLoading]);

  // Define the context menu items for different setlist types
  const getContextMenuItems = (setlistId, category) => {
    const menuItems = [
      {
        label: "Edit",
        action: () => navigate(`/setlist/${setlistId}`),
        icon: "edit"
      },
      {
        label: "Fork",
        action: () => alert(`Forking setlist ${setlistId} (mock function)`),
        icon: "copy"
      },
      {
        label: "Share",
        action: () => alert(`Sharing setlist ${setlistId} (mock function)`),
        icon: "share"
      }
    ];
    
    // Only show delete option for user's own setlists
    if (category === 'my') {
      menuItems.push({
        label: "Delete",
        action: () => {
          deleteSetlist(setlistId);
          alert(`Setlist ${setlistId} deleted`);
        },
        icon: "trash",
        danger: true
      });
    }
    
    return menuItems;
  };
  
  // This function is no longer needed as we're using inline dropdown menus now

  // Filter setlists based on search term
  const filterSetlists = (setlists) => {
    if (!searchTerm) return setlists;
    return setlists.filter(setlist => 
      setlist.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Helper to handle renaming a setlist
  const handleRename = (setlist) => {
    setEditingId(setlist._id);
    setEditingValue(setlist.name || "");
  };

  // Focus the input when editingId changes
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  // Commit rename
  const commitRename = async (setlist) => {
    const newName = editingValue.trim();
    if (newName && newName !== setlist.name) {
      try {
        logger.debug('SetlistsPage', `Saving setlist rename to MongoDB: ${setlist.name} → ${newName}`);
        
        // Create updated setlist object with new name
        const updatedSetlist = { ...setlist, name: newName };
        
        // Save to MongoDB
        await updateSetlist(setlist._id, updatedSetlist);
        
        // Also update local state for immediate UI response
        setMySetlists(prevSetlists =>
          prevSetlists.map(s => s._id === setlist._id ? { ...s, name: newName } : s)
        );
        
        logger.debug('SetlistsPage', 'Setlist rename saved successfully');
      } catch (error) {
        logger.error('SetlistsPage', 'Error updating setlist name:', error);
        alert('Failed to update setlist name. Please try again.');
      }
    }
    setEditingId(null);
    setEditingValue("");
  };

  // Cancel rename
  const cancelRename = () => {
    setEditingId(null);
    setEditingValue("");
  };

  // Helper to fork a setlist
  const handleFork = (setlist) => {
    // In a real implementation, this would call the API to fork the setlist
    alert(`Forking setlist ${setlist._id} (feature coming soon)`);
  };

  // Start editing a setlist (full edit with description and band assignment)
  const startEditSetlist = (setlist) => {
    setEditingSetlist(setlist);
    setEditForm({
      name: setlist.name || '',
      description: setlist.description || '',
      band_id: setlist.band_id || '',
      isPublic: setlist.isPublic !== false
    });
  };

  // Handle full setlist edit form submission
  const handleEditSetlist = async (e) => {
    e.preventDefault();
    if (!editingSetlist) return;

    try {
      const updatedSetlist = {
        ...editingSetlist,
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        band_id: editForm.band_id || null,
        isPublic: editForm.isPublic
      };

      await updateSetlist(editingSetlist._id, updatedSetlist);

      // Update local state
      setMySetlists(prevSetlists =>
        prevSetlists.map(s => s._id === editingSetlist._id ? updatedSetlist : s)
      );
      setBandSetlists(prevSetlists =>
        prevSetlists.map(s => s._id === editingSetlist._id ? updatedSetlist : s)
      );

      setEditingSetlist(null);
      setEditForm({ name: '', description: '', band_id: '', isPublic: true });
      showNotification('Setlist updated successfully', 'success');
    } catch (error) {
      console.error('Error updating setlist:', error);
      showNotification('Failed to update setlist', 'error');
    }
  };
  
  // Function for opening the delete confirmation dialog
  const confirmDeleteSetlist = (id) => {
    try {
      // Find the setlist in the mySetlists array
      const setlist = mySetlists.find(s => s._id === id);
      if (!setlist) return;
      
      const name = setlist.name || 'Untitled';
      
      // Show custom confirmation dialog
      setConfirmDialog({
        isOpen: true,
        title: "Delete Setlist",
        message: `Are you sure you want to delete "${name}"?`,
        onConfirm: async () => {
          try {
            await deleteSetlist(id);
            // Close the dialog
            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
            // Update local state
            setMySetlists(prevSetlists => 
              prevSetlists.filter(s => s._id !== id)
            );
          } catch (error) {
            console.error('Error deleting setlist:', error);
            alert('Failed to delete setlist. Please try again.');
            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          }
        },
        setlistId: id
      });
    } catch (error) {
      console.error('Error preparing delete confirmation:', error);
    }
  };

  const handleCreateSetlist = () => {
    logger.debug('SetlistsPage', 'handleCreateSetlist called');
    if (!isAuthenticated) {
      showAuthModal();
      return;
    }
    // Directly navigate to the new setlist page
    navigate('/setlist/new');
  };

  // Handle YouTube playlist import success
  const handleYouTubeImportSuccess = (result) => {
    if (result && result.setlist) {
      // Refresh the setlists to include the newly created one
      setMySetlists(prev => [result.setlist, ...prev]);
      showNotification(`Successfully imported playlist with ${result.summary.successfulSheets} sheets`, 'success');
      
      // Optionally navigate to the new setlist
      navigate(`/setlist/${result.setlist._id}`);
    }
  };



  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Setlists</h1>
        
        {/* Local action buttons */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={handleCreateSetlist}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
          >
            <PlusIcon className="w-3 h-3" />
            New Setlist
          </button>
          <button
            onClick={() => setShowYouTubeImportModal(true)}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
          >
            <Youtube className="w-3 h-3" />
            Import from YouTube
          </button>
        </div>
        
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search setlists..."
            className="w-full pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {isLoading ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground">Loading setlists...</p>
        </div>
      ) : (
        <Tabs defaultValue="other" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="other">All Setlists</TabsTrigger>
            <TabsTrigger value="band">My Bands' Setlists</TabsTrigger>
            <TabsTrigger value="my">My Setlists</TabsTrigger>
          </TabsList>
          
          <TabsContent value="my">
            {filterSetlists(mySetlists).length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
                <p className="text-muted-foreground">You don't have any setlists yet</p>
                <div className="flex gap-2 justify-center mt-4">
                  <Link to="/setlist/new" className="inline-block">
                    <Button variant="outline" size="sm">
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Create Setlist
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowYouTubeImportModal(true)}
                  >
                    <Youtube className="h-4 w-4 mr-2" />
                    Import from YouTube
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filterSetlists(mySetlists).map(setlist => (
                  <SetlistCard 
                    key={setlist._id}
                    setlist={setlist}
                    canManage={true}
                    context="setlists"
                    onRename={() => handleRename(setlist)}
                    onDuplicate={() => handleFork(setlist)}
                    onDelete={() => confirmDeleteSetlist(setlist._id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="band">
            {filterSetlists(bandSetlists).length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
                <p className="text-muted-foreground">No band setlists available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filterSetlists(bandSetlists).map(setlist => (
                  <SetlistCard 
                    key={setlist._id}
                    setlist={setlist}
                    canManage={false}
                    context="setlists"
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="other">
            {(() => {
              const allSetlists = filterSetlists([...mySetlists, ...otherSetlists, ...bandSetlists.filter(bs => !mySetlists.some(ms => ms._id === bs._id))]);
              const currentUserId = currentUser?._id;
              return allSetlists.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
                  <p className="text-muted-foreground">No setlists available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {allSetlists.map(setlist => {
                    const ownerId = setlist.owner?._id || setlist.owner;
                    const isOwner = currentUserId && ownerId && ownerId.toString() === currentUserId.toString();
                    return (
                      <SetlistCard
                        key={setlist._id}
                        setlist={setlist}
                        canManage={isOwner}
                        context="setlists"
                        onRename={isOwner ? () => handleRename(setlist) : undefined}
                        onDuplicate={isOwner ? () => handleFork(setlist) : undefined}
                        onDelete={isOwner ? () => confirmDeleteSetlist(setlist._id) : undefined}
                      />
                    );
                  })}
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>
      )}
      
      {/* Edit Setlist Form */}
      {editingSetlist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Edit Setlist</h2>
              <button
                onClick={() => {
                  setEditingSetlist(null);
                  setEditForm({ name: '', description: '', band_id: '', isPublic: true });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSetlist} className="space-y-4">
              <div>
                <label htmlFor="setlist-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="setlist-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="setlist-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="setlist-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  rows="3"
                  placeholder="Optional description for your setlist"
                />
              </div>

              <div>
                <label htmlFor="setlist-band" className="block text-sm font-medium text-gray-700 mb-1">
                  Assign to Band (Optional)
                </label>
                <select
                  id="setlist-band"
                  value={editForm.band_id}
                  onChange={(e) => setEditForm({ ...editForm, band_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Personal setlist (no band)</option>
                  {userBands.map(band => (
                    <option key={band._id} value={band._id}>
                      {band.name}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-gray-500 mt-1">
                  Personal setlists: Only you can vote • Band setlists: All band members can vote
                </div>
              </div>

              {/* Private toggle - only for premium users */}
              {currentUser?.isPremium && (
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Private
                    </label>
                    <div className="text-xs text-gray-500">
                      Only you and shared users can see this setlist
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, isPublic: !editForm.isPublic })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      !editForm.isPublic ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        !editForm.isPublic ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditingSetlist(null);
                    setEditForm({ name: '', description: '', band_id: '', isPublic: true });
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* YouTube Playlist Import Modal */}
      <YouTubePlaylistImportModal
        isOpen={showYouTubeImportModal}
        onClose={() => setShowYouTubeImportModal(false)}
        onSuccess={handleYouTubeImportSuccess}
      />
    </div>
  );
};

export default SetlistsPage;
